import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { wikiApi } from '../../api/wiki.js';

const RECENT_KEY = 'bos.wiki.recentSearches';
const MAX_RECENT = 10;

export default function WikiSearch() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
  });
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!q.trim()) { setItems([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const out = await wikiApi.search({ q });
        setItems(out?.items || []);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  function rememberQuery(query) {
    if (!query.trim()) return;
    const next = [query, ...recent.filter((r) => r !== query)].slice(0, MAX_RECENT);
    setRecent(next);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* */ }
  }

  return (
    <section style={{ padding: '24px 32px', maxWidth: 800, margin: '0 auto' }} data-testid="wiki-search">
      <h1>Search the wiki</h1>
      <input
        ref={inputRef}
        type="search"
        placeholder="Search by keyword…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onBlur={() => rememberQuery(q)}
        aria-label="Wiki search"
        data-testid="wiki-search-input"
        style={{ width: '100%', fontSize: 18, padding: 12 }}
      />

      {!q && recent.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="muted small" style={{ marginBottom: 6 }}>Recent searches</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {recent.map((r, i) => (
              <button key={i} type="button" className="btn ghost small" onClick={() => setQ(r)} style={{ background: '#f1f5f9' }}>
                {r}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="muted" style={{ marginTop: 16 }}>Searching…</div>}

      {!loading && q && items.length === 0 && (
        <div className="card" style={{ marginTop: 16, textAlign: 'center', padding: 32 }}>
          <p className="muted">No results for "{q}". Try different keywords.</p>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {items.map((it) => (
          <Link
            key={it.page_id}
            to={`/wiki/${it.space_key}/${it.slug}`}
            className="card"
            data-testid="search-result"
            data-page-id={it.page_id}
            style={{ display: 'block', padding: 14, textDecoration: 'none', color: 'inherit', marginBottom: 8 }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 20 }}>{it.emoji_icon || '📄'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{it.title}</div>
                <div className="muted small">
                  <span className="chip" style={{ fontSize: 10, marginRight: 6 }}>{it.space_key}</span>
                  {it.last_edited_by_name && <>by {it.last_edited_by_name} · </>}
                  {timeAgo(it.updated_at)}
                </div>
              </div>
            </div>
            {it.snippet && (
              <p
                style={{ marginTop: 6, fontSize: 13, color: '#475569' }}
                dangerouslySetInnerHTML={{ __html: it.snippet }}
              />
            )}
          </Link>
        ))}
      </div>
    </section>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}
